import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { ACCESS_TYPE_OPTIONS, EMPTY_PACK_FORM, GIF_STATUS_FORM_OPTIONS } from './gifConstants';

export default function GifPackEditModal({ open, packId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_PACK_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(packId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        if (packId) {
          const data = await adminApi.getImGifPack(packId);
          if (cancelled) {
            return;
          }
          setForm({
            name: data.name || '',
            accessType: data.accessType || 'FREE',
            coverObjectKey: data.coverObjectKey || '',
            description: data.description || '',
            status: data.status || 'ACTIVE',
            sort: data.sort ?? 0,
            tags: data.tags || '',
          });
        } else {
          setForm({ ...EMPTY_PACK_FORM });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, packId]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCoverUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError('');
    try {
      const key = await adminApi.uploadOssImage(file);
      updateField('coverObjectKey', key);
    } catch (err) {
      setError(err.message || '封面上傳失敗');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!form.name?.trim()) {
      setError('名稱必填');
      return;
    }
    const payload = {
      name: form.name.trim(),
      accessType: form.accessType || 'FREE',
      coverObjectKey: form.coverObjectKey || null,
      description: form.description?.trim() || null,
      status: form.status,
      sort: Number(form.sort) || 0,
      tags: form.tags?.trim() || null,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateImGifPack(packId, payload);
      } else {
        await adminApi.createImGifPack(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '編輯 GIF 包' : '新增 GIF 包'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : (
          <form className="form-grid" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <label>
              名稱
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </label>
            <label>
              Access
              <select
                value={form.accessType}
                onChange={(e) => updateField('accessType', e.target.value)}
              >
                {ACCESS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              狀態
              <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                {GIF_STATUS_FORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              排序
              <input
                type="number"
                value={form.sort}
                onChange={(e) => updateField('sort', e.target.value)}
              />
            </label>
            <label>
              標籤
              <input
                type="text"
                value={form.tags}
                onChange={(e) => updateField('tags', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              描述
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              封面
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input type="file" accept="image/*" onChange={handleCoverUpload} disabled={uploading} />
                {form.coverObjectKey ? (
                  <img
                    src={resolveAdminMediaUrl(form.coverObjectKey)}
                    alt=""
                    style={{ width: 64, height: 64, objectFit: 'cover' }}
                  />
                ) : null}
              </div>
              <small>{form.coverObjectKey || '尚未上傳'}</small>
            </label>

            <div className="modal-panel__footer form-grid__full">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                取消
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting || uploading}>
                {submitting ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
